import React, { useState } from 'react';
import { ChevronDown, ChevronRight, X } from 'lucide-react';

// Small building blocks for the App View (bottom-tab shell, AdminAppLayout).

// A section that folds away to one tappable line in App View. In the web view it renders its
// children unchanged, so wrapping a desktop section in it costs nothing there.
export function AppFold({ isAppView, title, summary, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!isAppView) return children;
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', minHeight: 50,
          background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', color: 'var(--text)',
        }}
      >
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontWeight: 700, fontSize: '.88rem' }}>{title}</span>
          {summary && (
            <span style={{ display: 'block', fontSize: '.74rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {summary}
            </span>
          )}
        </span>
        {open ? <ChevronDown size={18} color="var(--text-muted)" /> : <ChevronRight size={18} color="var(--text-muted)" />}
      </button>
      {open && <div style={{ borderTop: '1px solid var(--border)', padding: 8 }}>{children}</div>}
    </div>
  );
}

// Bottom sheet: dims the page, slides content up from the bottom, closes on the backdrop or X.
export function AppSheet({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.5)' }} />
      <div
        role="dialog"
        aria-label={title}
        style={{
          position: 'relative', background: 'var(--bg-card)', color: 'var(--text)', borderRadius: '16px 16px 0 0',
          maxHeight: '85vh', overflowY: 'auto', padding: '10px 14px calc(18px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ flex: 1, fontWeight: 700, fontSize: '.95rem' }}>{title}</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ width: 44, height: 44, border: 'none', background: 'none', color: 'var(--text-muted)', display: 'grid', placeItems: 'center', cursor: 'pointer' }}
          >
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// One full-width row in an AppSheet list (48px tall, icon + label).
export function SheetRow({ icon: Icon, label, onClick, danger, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 12, minHeight: 48, padding: '0 10px',
        border: 'none', borderBottom: '1px solid var(--border)', background: 'none', textAlign: 'left',
        color: danger ? 'var(--danger, #dc2626)' : 'var(--text)', fontWeight: 600, fontSize: '.88rem',
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? .55 : 1,
      }}
    >
      {Icon && <Icon size={18} />}
      {label}
    </button>
  );
}
